import React, {
  useCallback,
  useState,
  useContext,
  useRef,
  useMemo,
} from 'react';
import T from 'i18n-react';
import DialogContentText from '@material-ui/core/DialogContentText';
import useIsMounted from 'ismounted';
import { fetchJson } from '../../../core/fetch';
import { useSnackbar } from 'notistack';
import { API_V1_BUSINESS_FOLLOW } from '../../routing';
import { AUTH_PARAM_KEYS, ACTION_VALUE } from '~/models/Auth/AuthParamModel';
import AppDialog from '../../../core/Dialog/AppDialog';
import { AuthDialogContext } from '../../components/AuthDialog/AuthDialogContextProvider';
import FollowChip from './components/FollowChip';
import SquareButton from './components/SquareButton';

type FollowButtonProps = {
  businessId: number,
  businessName: string,
  followerCount?: number,
  isFollowing?: boolean,
  buttonType?: 'rounded',
  isFollowerShown?: boolean,
  isAuthenticated?: boolean,
  onChange?: (id: number, follow: boolean, followerCount: number) => void,
  classes?: { [key: string]: string },
  children?: React.ReactNode,
};

const FollowButton = ({
  businessId: idProps,
  businessName: nameProps,
  followerCount: countProps = 0,
  isFollowing: isFollowingProps,
  buttonType,
  isFollowerShown,
  isAuthenticated,
  onChange,
  classes,
  children,
}: FollowButtonProps) => {
  const [state, setState] = useState({
    id: idProps,
    name: nameProps,
    count: countProps,
    isFollowing: Boolean(isFollowingProps),
    mouseEnter: false,
    mouseLeft: true,
  });
  const { handleOpen: handleOpenAuthDialog } = useContext(AuthDialogContext);

  const isMounted = useIsMounted();
  const alertDialogRef = useRef();
  const { enqueueSnackbar } = useSnackbar();
  const { id, name, count, isFollowing, mouseEnter, mouseLeft } = state;

  const updateState = (data = {}) =>
    setState((prevState) => ({ ...prevState, ...data }));

  const authParams = useMemo(
    () => ({
      [AUTH_PARAM_KEYS.ACTION]: ACTION_VALUE.FOLLOW,
      [AUTH_PARAM_KEYS.BUSINESS_ID]: id,
    }),
    [id],
  );

  const switchFollow = useCallback(
    (url, method, isFollowing, message) => {
      fetchJson(url, {
        init: { method },
        onSuccess: ({ json: { followerCount } }) => {
          if (isMounted.current) {
            updateState({
              count: followerCount,
              isFollowing,
              mouseEnter: false,
              mouseLeft: false,
            });
            message &&
              enqueueSnackbar(message, {
                variant: isFollowing ? 'success' : 'default',
              });
            onChange?.(id, isFollowing, followerCount);
          }
        },
        onError: ({ messages = [T.translate('request.error')] }) =>
          isMounted.current &&
          messages.forEach((m) => enqueueSnackbar(m, { variant: 'error' })),
      });
    },
    [enqueueSnackbar, id, isMounted, onChange],
  );

  const handleUnfollow = useCallback(
    (event, onSuccess, onError) => {
      onSuccess();
      switchFollow(
        API_V1_BUSINESS_FOLLOW.replace(':id', id),
        'DELETE',
        false,
        T.translate('business.unfollowed'),
      );
    },
    [switchFollow, id],
  );

  const handleClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (isAuthenticated) {
        if (isFollowing) {
          alertDialogRef.current?.handleOpen?.();
        } else {
          switchFollow(
            API_V1_BUSINESS_FOLLOW.replace(':id', id),
            'PATCH',
            true,
            T.translate('business.followed'),
          );
        }
      } else {
        handleOpenAuthDialog({ authParams });
      }
    },
    [
      authParams,
      handleOpenAuthDialog,
      id,
      isAuthenticated,
      isFollowing,
      switchFollow,
    ],
  );

  const handleCloseDialog = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const isMouseEntered = isFollowing && mouseEnter && mouseLeft;

  return (
    <>
      <div>
        {buttonType === 'rounded' ? (
          <FollowChip
            size="small"
            checked={isFollowing}
            onClick={handleClick}
            label={
              isFollowing
                ? T.translate('business.following')
                : T.translate('business.follow')
            }
            classes={classes}
          />
        ) : (
          <SquareButton
            following={isFollowing}
            mouseEnter={mouseEnter}
            isMouseEntered={isMouseEntered}
            disableElevation
            classes={classes}
            children={children}
            onClick={handleClick}
            {...(isFollowing && {
              onMouseLeave: () =>
                updateState({ mouseEnter: false, mouseLeft: true }),
              ...(mouseLeft && {
                onMouseEnter: () => updateState({ mouseEnter: true }),
              }),
            })}
            {...(!!isFollowerShown &&
              !!count && {
                totalCount: count,
              })}
          />
        )}
      </div>
      <AppDialog
        title={T.translate('business.unfollowDialog.title', { name })}
        contentNode={
          <DialogContentText>
            {T.translate('business.unfollowDialog.content')}
          </DialogContentText>
        }
        onAccept={handleUnfollow}
        onClose={handleCloseDialog}
        DialogProps={{ maxWidth: 500 }}
        actionButtonText={T.translate('business.unfollow')}
        cancelButtonText={T.translate('cancel')}
        ActionButtonProps={{ variant: 'text' }}
        ref={alertDialogRef}
      />
    </>
  );
};

export default FollowButton;
